import { signOut, useSession } from "@internal/auth";
import { AppShell, NavItem, Sidebar } from "@internal/ui";
import {
  Archive,
  Calendar,
  LayoutTemplate,
  Repeat,
  Settings,
} from "lucide-react";
import { useState } from "react";
import { Link, Outlet, useLocation, useParams } from "react-router-dom";
import { CycleModal } from "@/components/CycleModal";
import { freeWeekCandidates, NewWeekModal } from "@/components/NewWeekModal";
import { SyncStatus } from "@/components/shared/SyncStatus";
import { UpdateBanner } from "@/components/shared/UpdateBanner";
import { useAllCycles } from "@/hooks/useCycles";
import { useSettings } from "@/hooks/useSettings";
import { useAllWeeks, useOpenWeeks } from "@/hooks/useWeeks";
import { cycleLabel, isCycleEnded } from "@/lib/cycles";
import { todayDayKeyInRange } from "@/lib/days";

function Brand() {
  return (
    <Link to="/" className="brand" data-testid="brand-home">
      <img src="/icon.svg" alt="" className="brand-icon" />
      <span className="brand-mark">Dagbok</span>
    </Link>
  );
}

function SidebarContent({
  onOpenNewWeek,
  onOpenNewCycle,
}: {
  onOpenNewWeek: () => void;
  onOpenNewCycle: () => void;
}) {
  const { user } = useSession();
  const { weeks: openWeeks } = useOpenWeeks();
  const { weeks: allWeeks } = useAllWeeks();
  const { cycles } = useAllCycles();
  const activeCycles = cycles.filter((c) => !c.data.archived);
  const params = useParams<{ weekId?: string; cycleId?: string }>();
  const location = useLocation();
  const free = freeWeekCandidates(allWeeks);
  const ctaLabel =
    free.length === 1 ? `Ny uke (${free[0].weekNumber})` : "+ Ny uke";

  return (
    <Sidebar>
      <Sidebar.Top>
        {free.length > 0 ? (
          <button
            type="button"
            className="btn-primary new-week-cta"
            onClick={onOpenNewWeek}
            data-testid="new-week-button"
          >
            {ctaLabel}
          </button>
        ) : null}
        {openWeeks.length === 0 ? (
          <div className="nav-empty" data-testid="open-weeks-empty">
            Ingen åpne uker enda.
          </div>
        ) : (
          openWeeks.map((w) => {
            const active = params.weekId === w.id;
            const todayKey = todayDayKeyInRange(w.data.startDate, new Date());
            const encoded = encodeURIComponent(w.id);
            const to = todayKey
              ? `/uke/${encoded}/${todayKey}`
              : `/uke/${encoded}`;
            return (
              <NavItem
                key={w.id}
                to={to}
                label={`Uke ${w.data.weekNumber} · ${w.data.year}`}
                icon={<Calendar size={16} />}
                active={active}
                data-testid={`open-week-link-${w.id}`}
              />
            );
          })
        )}
        <div className="nav-section-label">Sykluser</div>
        <button
          type="button"
          className="btn-ghost new-cycle-cta"
          onClick={onOpenNewCycle}
          data-testid="new-cycle-button"
        >
          + Ny syklus
        </button>
        {activeCycles.length === 0 ? (
          <div className="nav-empty" data-testid="active-cycles-empty">
            Ingen aktive sykluser.
          </div>
        ) : (
          activeCycles.map((c) => (
            <NavItem
              key={c.id}
              to={`/syklus/${encodeURIComponent(c.id)}`}
              label={cycleLabel(c.data)}
              icon={<Repeat size={16} />}
              active={params.cycleId === c.id}
              badge={isCycleEnded(c.data, new Date()) ? "avsluttet" : undefined}
              badgeTestId={`cycle-ended-marker-${c.id}`}
              data-testid={`cycle-link-${c.id}`}
            />
          ))
        )}
      </Sidebar.Top>
      <Sidebar.Bottom>
        <NavItem
          to="/arkiv"
          label="Arkiv"
          icon={<Archive size={16} />}
          active={location.pathname === "/arkiv"}
          data-testid="nav-arkiv"
        />
        <NavItem
          to="/ukesmal"
          label="Ukesmal"
          icon={<LayoutTemplate size={16} />}
          active={location.pathname === "/ukesmal"}
          data-testid="nav-week-template"
        />
        <NavItem
          to="/innstillinger"
          label="Innstillinger"
          icon={<Settings size={16} />}
          active={location.pathname === "/innstillinger"}
          data-testid="nav-innstillinger"
        />
      </Sidebar.Bottom>
      <Sidebar.User
        name={user?.email ?? ""}
        onSignOut={() => signOut()}
        data-testid="signout-button"
      />
    </Sidebar>
  );
}

export function Layout() {
  const [modalOpen, setModalOpen] = useState(false);
  const [cycleModalOpen, setCycleModalOpen] = useState(false);
  const { settings } = useSettings();
  const { cycles } = useAllCycles();

  return (
    <>
      <UpdateBanner />
      <AppShell
        brand={<Brand />}
        sidebar={
          <SidebarContent
            onOpenNewWeek={() => setModalOpen(true)}
            onOpenNewCycle={() => setCycleModalOpen(true)}
          />
        }
        status={<SyncStatus />}
      >
        <Outlet />
      </AppShell>
      <NewWeekModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        settings={settings}
      />
      {cycleModalOpen ? (
        <CycleModal cycles={cycles} onClose={() => setCycleModalOpen(false)} />
      ) : null}
    </>
  );
}
